<!-- Customer Edit Page - Accordion Layout -->
<div class="customer-edit-page">
  <!-- Loading State (includes header skeleton) -->
  @if (loading()) {
    <app-customer-edit-skeleton />
  } @else if (error()) {
    <!-- Error State -->
    <div class="error-container">
      <i class="pi pi-exclamation-circle"></i>
      <span>{{ error() }}</span>
      <p-button label="Retry" severity="secondary" (onClick)="retry()" />
    </div>
  } @else {
    <!-- Page Header with Save Button -->
    <div class="page-header">
      <div class="header-row">
        <div class="header-left">
          <p-button
            icon="pi pi-arrow-left"
            [text]="true"
            [rounded]="true"
            severity="secondary"
            pTooltip="Back to List"
            tooltipPosition="right"
            (onClick)="goBack()"
          />
          <div class="title-section">
            <h1 class="page-title">{{ pageTitle() }}</h1>
            <span class="customer-id">ID: {{ customerId() }}</span>
          </div>
        </div>
        <div class="header-actions">
          <p-button
            label="History"
            icon="pi pi-history"
            size="small"
            severity="secondary"
            [outlined]="true"
            (onClick)="openCustomerHistoryDialog()"
            pTooltip="View customer activity history"
            tooltipPosition="bottom"
          />
          <p-button
            label="Save"
            icon="pi pi-save"
            size="small"
            [loading]="saving()"
            [disabled]="formGroup.invalid || !hasUnsavedChanges()"
            (onClick)="save()"
          />
        </div>
      </div>
      @if (hasUnsavedChanges()) {
        <div class="unsaved-indicator">
          <i class="pi pi-circle-fill"></i>
          Unsaved changes
        </div>
      }
    </div>

    <!-- Form Content with Accordions -->
    <form [formGroup]="formGroup" class="edit-form">
      <div class="accordion-container">
        <p-accordion [multiple]="true" [activeIndex]="accordionActiveIndices()">
          <!-- Accordion 0: Basic Information -->
          <p-accordionpanel value="0">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-user"></i>
                <span>Basic Info</span>
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-body">
                <div class="form-grid">
                  <!-- First Name -->
                  <div class="form-field">
                    <label for="firstName" class="form-label">
                      First Name <span class="required">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      pInputText
                      [formControl]="formGroup.controls.firstName"
                      [class.ng-invalid]="hasError('firstName')"
                      placeholder="Enter first name"
                    />
                    @if (hasError("firstName")) {
                      <small class="error-message">{{
                        getErrorMessage("firstName")
                      }}</small>
                    }
                  </div>

                  <!-- Last Name -->
                  <div class="form-field">
                    <label for="lastName" class="form-label">
                      Last Name <span class="required">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      pInputText
                      [formControl]="formGroup.controls.lastName"
                      [class.ng-invalid]="hasError('lastName')"
                      placeholder="Enter last name"
                    />
                    @if (hasError("lastName")) {
                      <small class="error-message">{{
                        getErrorMessage("lastName")
                      }}</small>
                    }
                  </div>

                  <!-- Email -->
                  <div class="form-field">
                    <label for="email" class="form-label">
                      Email <span class="required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      pInputText
                      [formControl]="formGroup.controls.email"
                      [class.ng-invalid]="hasError('email')"
                      placeholder="Enter email address"
                    />
                    @if (hasError("email")) {
                      <small class="error-message">{{
                        getErrorMessage("email")
                      }}</small>
                    }
                  </div>

                  <!-- Phone -->
                  <div class="form-field">
                    <label for="phone" class="form-label">Phone</label>
                    <input
                      id="phone"
                      type="tel"
                      pInputText
                      [formControl]="formGroup.controls.phone"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <!-- Company -->
                  <div class="form-field">
                    <label for="company" class="form-label">Company</label>
                    <input
                      id="company"
                      type="text"
                      pInputText
                      [formControl]="formGroup.controls.company"
                      placeholder="Enter company name"
                    />
                  </div>

                  <!-- External ID (Platform Customer ID) -->
                  <div class="form-field">
                    <label for="externalId" class="form-label"
                      >External ID</label
                    >
                    <input
                      id="externalId"
                      type="text"
                      pInputText
                      [formControl]="formGroup.controls.externalId"
                      placeholder="Platform customer ID"
                    />
                  </div>
                </div>
              </div>
            </p-accordioncontent>
          </p-accordionpanel>

          <!-- Accordion 1: Addresses (Shipping + Billing side by side) -->
          <p-accordionpanel value="1">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-map-marker"></i>
                <span>Addresses</span>
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-body">
                <!-- Address Sync Conflict Message -->
                @if (addressSyncConflict()) {
                  <p-message
                    severity="warn"
                    [text]="addressSyncConflict()!"
                    styleClass="address-conflict-message"
                  />
                }
                <div class="addresses-container">
                  <!-- Shipping Address Column -->
                  <div class="address-column" formGroupName="shipping">
                    <div class="address-header">
                      <i class="pi pi-truck"></i>
                      <span>Shipping Address</span>
                    </div>

                    <!-- Same as billing checkbox -->
                    <div class="same-as-address">
                      <p-checkbox
                        [formControl]="
                          formGroup.controls.shippingMatchesBilling
                        "
                        [binary]="true"
                        inputId="shippingMatchesBilling"
                      />
                      <label
                        for="shippingMatchesBilling"
                        class="checkbox-label"
                      >
                        Same as billing address
                      </label>
                    </div>

                    <div
                      class="address-form-grid"
                      [class.disabled]="
                        formGroup.controls.shippingMatchesBilling.value
                      "
                    >
                      <!-- First Name -->
                      <div class="form-field">
                        <label for="shippingFirstName" class="form-label"
                          >First Name</label
                        >
                        <input
                          id="shippingFirstName"
                          type="text"
                          pInputText
                          formControlName="firstName"
                          placeholder="First name"
                        />
                      </div>

                      <!-- Last Name -->
                      <div class="form-field">
                        <label for="shippingLastName" class="form-label"
                          >Last Name</label
                        >
                        <input
                          id="shippingLastName"
                          type="text"
                          pInputText
                          formControlName="lastName"
                          placeholder="Last name"
                        />
                      </div>

                      <!-- Street 1 -->
                      <div class="form-field full-width">
                        <label for="shippingStreet1" class="form-label"
                          >Street Address</label
                        >
                        <input
                          id="shippingStreet1"
                          type="text"
                          pInputText
                          formControlName="street1"
                          placeholder="Street address"
                        />
                      </div>

                      <!-- Street 2 -->
                      <div class="form-field full-width">
                        <label for="shippingStreet2" class="form-label"
                          >Street Address 2</label
                        >
                        <input
                          id="shippingStreet2"
                          type="text"
                          pInputText
                          formControlName="street2"
                          placeholder="Apt, suite, unit, etc."
                        />
                      </div>

                      <!-- Country -->
                      <div class="form-field">
                        <label for="shippingCountry" class="form-label"
                          >Country</label
                        >
                        <p-select
                          id="shippingCountry"
                          formControlName="country"
                          [options]="countryOptions"
                          optionLabel="label"
                          optionValue="alpha2"
                          [filter]="true"
                          filterBy="name,alpha2"
                          placeholder="Select country"
                          [showClear]="true"
                          styleClass="w-full"
                          appendTo="body"
                          (onChange)="onShippingCountryChange()"
                        />
                      </div>

                      <!-- State -->
                      <div class="form-field">
                        <label for="shippingState" class="form-label"
                          >State / Province</label
                        >
                        @if (isShippingUSA()) {
                          <p-select
                            id="shippingState"
                            formControlName="state"
                            [options]="stateOptions"
                            optionLabel="label"
                            optionValue="abbreviation"
                            [filter]="true"
                            filterBy="name,abbreviation"
                            placeholder="Select state"
                            [showClear]="true"
                            styleClass="w-full"
                            appendTo="body"
                          />
                        } @else {
                          <input
                            id="shippingState"
                            type="text"
                            pInputText
                            formControlName="state"
                            placeholder="State / Province"
                          />
                        }
                      </div>

                      <!-- City -->
                      <div class="form-field">
                        <label for="shippingCity" class="form-label"
                          >City</label
                        >
                        <input
                          id="shippingCity"
                          type="text"
                          pInputText
                          formControlName="city"
                          placeholder="City"
                        />
                      </div>

                      <!-- Postcode -->
                      <div class="form-field">
                        <label for="shippingPostcode" class="form-label"
                          >Postal Code</label
                        >
                        <input
                          id="shippingPostcode"
                          type="text"
                          pInputText
                          formControlName="postcode"
                          placeholder="Postal code"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Billing Address Column -->
                  <div
                    class="address-column billing-column"
                    formGroupName="billing"
                  >
                    <div class="address-header">
                      <i class="pi pi-file"></i>
                      <span>Billing Address</span>
                    </div>

                    <!-- Same as shipping checkbox -->
                    <div class="same-as-address">
                      <p-checkbox
                        [formControl]="
                          formGroup.controls.billingMatchesShipping
                        "
                        [binary]="true"
                        inputId="billingMatchesShipping"
                      />
                      <label
                        for="billingMatchesShipping"
                        class="checkbox-label"
                      >
                        Same as shipping address
                      </label>
                    </div>

                    <div
                      class="address-form-grid"
                      [class.disabled]="
                        formGroup.controls.billingMatchesShipping.value
                      "
                    >
                      <!-- First Name -->
                      <div class="form-field">
                        <label for="billingFirstName" class="form-label"
                          >First Name</label
                        >
                        <input
                          id="billingFirstName"
                          type="text"
                          pInputText
                          formControlName="firstName"
                          placeholder="First name"
                        />
                      </div>

                      <!-- Last Name -->
                      <div class="form-field">
                        <label for="billingLastName" class="form-label"
                          >Last Name</label
                        >
                        <input
                          id="billingLastName"
                          type="text"
                          pInputText
                          formControlName="lastName"
                          placeholder="Last name"
                        />
                      </div>

                      <!-- Street 1 -->
                      <div class="form-field full-width">
                        <label for="billingStreet1" class="form-label"
                          >Street Address</label
                        >
                        <input
                          id="billingStreet1"
                          type="text"
                          pInputText
                          formControlName="street1"
                          placeholder="Street address"
                        />
                      </div>

                      <!-- Street 2 -->
                      <div class="form-field full-width">
                        <label for="billingStreet2" class="form-label"
                          >Street Address 2</label
                        >
                        <input
                          id="billingStreet2"
                          type="text"
                          pInputText
                          formControlName="street2"
                          placeholder="Apt, suite, unit, etc."
                        />
                      </div>

                      <!-- Country -->
                      <div class="form-field">
                        <label for="billingCountry" class="form-label"
                          >Country</label
                        >
                        <p-select
                          id="billingCountry"
                          formControlName="country"
                          [options]="countryOptions"
                          optionLabel="label"
                          optionValue="alpha2"
                          [filter]="true"
                          filterBy="name,alpha2"
                          placeholder="Select country"
                          [showClear]="true"
                          styleClass="w-full"
                          appendTo="body"
                          (onChange)="onBillingCountryChange()"
                        />
                      </div>

                      <!-- State -->
                      <div class="form-field">
                        <label for="billingState" class="form-label"
                          >State / Province</label
                        >
                        @if (isBillingUSA()) {
                          <p-select
                            id="billingState"
                            formControlName="state"
                            [options]="stateOptions"
                            optionLabel="label"
                            optionValue="abbreviation"
                            [filter]="true"
                            filterBy="name,abbreviation"
                            placeholder="Select state"
                            [showClear]="true"
                            styleClass="w-full"
                            appendTo="body"
                          />
                        } @else {
                          <input
                            id="billingState"
                            type="text"
                            pInputText
                            formControlName="state"
                            placeholder="State / Province"
                          />
                        }
                      </div>

                      <!-- City -->
                      <div class="form-field">
                        <label for="billingCity" class="form-label">City</label>
                        <input
                          id="billingCity"
                          type="text"
                          pInputText
                          formControlName="city"
                          placeholder="City"
                        />
                      </div>

                      <!-- Postcode -->
                      <div class="form-field">
                        <label for="billingPostcode" class="form-label"
                          >Postal Code</label
                        >
                        <input
                          id="billingPostcode"
                          type="text"
                          pInputText
                          formControlName="postcode"
                          placeholder="Postal code"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </p-accordioncontent>
          </p-accordionpanel>

          <!-- Accordion 2: Payment Methods -->
          <p-accordionpanel value="2">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-credit-card"></i>
                <span>Payment Methods</span>
                <span class="payment-count">{{ paymentMethods().length }}</span>
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-body payment-section">
                <app-customer-payment-methods-section
                  [paymentMethods]="paymentMethods()"
                  [loading]="paymentMethodsLoading()"
                  [readOnly]="false"
                  [showDelete]="true"
                  (addMethod)="openAddPaymentMethodDialog()"
                  (deleteMethod)="onDeletePaymentMethod($event)"
                />
              </div>
            </p-accordioncontent>
          </p-accordionpanel>
        </p-accordion>
      </div>
    </form>
  }
</div>

<!-- Toast for notifications -->
<p-toast />

<!-- Confirm dialog for unsaved changes -->
<p-confirmDialog />
